import React, { useRef, useState, useEffect, useContext } from "react";
import { InterviewContext } from "../../../context/InterviewProvider";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { toBlobURL, fetchFile } from "@ffmpeg/util";
import { toast } from "react-toastify";
import { ImageSegmenter, FilesetResolver } from "@mediapipe/tasks-vision";

const InterviewMediaRecorder = ({
  closeTrigger = false,
  header = null,
  onRemoveUpload,
  onUpload,
  recordedVideoBlobUrlValue = null,
  setIsLoadingOverlay,
}) => {
  const interviewContext = useContext(InterviewContext);
  const videoRef = useRef(null);
  const ffmpegRef = useRef(new FFmpeg());
  const [mediaStream, setMediaStream] = useState(null);
  const [isVideoRecorded, setIsVideoRecorded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [convertedVideoUrl, setConvertedVideoUrl] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null); // Updated to manage MediaRecorder instance
  const [mediaBlobUrl, setMediaBlobUrl] = useState(null);

  // NEW
  const canvasRef = useRef(null);
  const segmentationRef = useRef(null);
  const [blurBackground, setBlurBackground] = useState(false);
  const [isSegmentationActive, setIsSegmentationActive] = useState(false);

  // ============================ Segmentaion ============================
  useEffect(() => {
    if (interviewContext.isBlurBackgroundEnabled && !blurBackground) {
      setBlurBackground(true);
    }

    if (!interviewContext.isBlurBackgroundEnabled && blurBackground) {
      setBlurBackground(false);
    }
  }, [interviewContext.isBlurBackgroundEnabled]);

  const startSegmentation = async () => {
    if (isSegmentationActive) return;

    const vision = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
    );

    const segmenter = await ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter_landscape/float16/1/selfie_segmenter_landscape.tflite",
        delegate: "CPU",
      },
      runningMode: "VIDEO",
      outputCategoryMask: true,
    });

    segmentationRef.current = segmenter;

    const offScreenCanvas = document.createElement("canvas");
    offScreenCanvas.width = canvasRef.current.width;
    offScreenCanvas.height = canvasRef.current.height;
    const offCtx = offScreenCanvas.getContext("2d");

    let lastSegmentationMask = null;

    const processFrame = () => {
      if (!segmentationRef.current || !videoRef.current || !canvasRef.current)
        return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const startTimeMs = performance.now();

      // Perform segmentation
      const segmentation = segmentationRef.current.segmentForVideo(
        videoRef.current,
        startTimeMs
      );

      // Reuse the last segmentation mask if the current one fails
      if (segmentation?.categoryMask) {
        lastSegmentationMask = segmentation.categoryMask.getAsFloat32Array();
      } else if (!lastSegmentationMask) {
        requestAnimationFrame(processFrame);
        return;
      }

      const mask = lastSegmentationMask;

      // 1. Draw the blurred background
      ctx.filter = "blur(15px)";
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      ctx.filter = "none";

      // 2. Prepare the segmentation mask for the human
      offCtx.clearRect(0, 0, offScreenCanvas.width, offScreenCanvas.height);
      offCtx.drawImage(
        videoRef.current,
        0,
        0,
        offScreenCanvas.width,
        offScreenCanvas.height
      );
      const personData = offCtx.getImageData(
        0,
        0,
        offScreenCanvas.width,
        offScreenCanvas.height
      );

      const threshold = 0.8; // Adjust for sharper boundaries
      for (let i = 0; i < mask.length; i++) {
        const j = i * 4;
        const maskValue = mask[i] <= threshold ? 255 : 0; // Invert logic to isolate the human

        if (maskValue === 0) {
          // Make non-human (background) pixels fully transparent
          personData.data[j + 3] = 0;
        }
      }
      offCtx.putImageData(personData, 0, 0);

      // 3. Overlay the clear human on top of the blurred background
      ctx.drawImage(offScreenCanvas, 0, 0, canvas.width, canvas.height);

      requestAnimationFrame(processFrame);
    };

    setIsSegmentationActive(true);
    processFrame();
    interviewContext.setBlurBackgroundState(true);
  };

  const stopSegmentation = () => {
    segmentationRef.current = null;
    setIsSegmentationActive(false);
    interviewContext.setBlurBackgroundState(false);
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.addEventListener("loadedmetadata", () => {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
      });
    }
  }, []);

  useEffect(() => {
    if (blurBackground) {
      startSegmentation();
    } else {
      stopSegmentation();
    }
    return () => stopSegmentation();
  }, [blurBackground]);

  const startDrawingLoop = () => {
    const drawFrame = () => {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      if (!canvas || !video) {
        requestAnimationFrame(drawFrame);
        return;
      }

      const ctx = canvas.getContext("2d");
      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      ctx.restore();

      requestAnimationFrame(drawFrame);
    };
    drawFrame();
  };
  // ============================ End ============================

  useEffect(() => {
    return () => {
      handleStopRecording();
    };
  }, []);

  useEffect(() => {
    const updateConvertedUrl = async () => {
      setIsLoadingOverlay(true);
      const convertedUrl = await fixWebMFormat();
      if (convertedUrl) {
        setConvertedVideoUrl(convertedUrl);
        interviewContext.setConvertedVideoUrl(convertedUrl);
      }
      setIsLoadingOverlay(false);
    };
    if (mediaBlobUrl) {
      updateConvertedUrl();
    }
  }, [mediaBlobUrl]);

  // Load FFmpeg
  useEffect(() => {
    const loadFFmpeg = async () => {
      setIsLoading(true);
      const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd";
      const ffmpeg = ffmpegRef.current;
      ffmpeg.on("log", ({ message }) => {});
      await ffmpeg.load({
        coreURL: await toBlobURL(
          `${baseURL}/ffmpeg-core.js`,
          "text/javascript"
        ),
        wasmURL: await toBlobURL(
          `${baseURL}/ffmpeg-core.wasm`,
          "application/wasm"
        ),
      });
      setLoaded(true);
      setIsLoading(false);
    };

    if (ffmpegRef) {
      if (!loaded) {
        loadFFmpeg();
      }
    }
  }, [ffmpegRef]);

  // Handle Interview Status
  useEffect(() => {
    if (
      interviewContext.interviewStatus === "start" &&
      mediaStream &&
      !isRecording &&
      interviewContext?.blurBackgroundState
    ) {
      startRecording();
    }

    if (interviewContext.interviewStatus === "stop") {
      handleStopRecording();
    }

    if (interviewContext.interviewStatus === "end") {
      handleStopRecording();
    }
  }, [
    interviewContext.interviewStatus,
    mediaStream,
    isRecording,
    interviewContext.blurBackgroundState,
  ]);

  useEffect(() => {
    if (convertedVideoUrl && interviewContext.interviewStatus === "end") {
      interviewContext.setInterviewStatus("completed");
    }
    if (convertedVideoUrl && interviewContext.interviewStatus === "stop") {
      interviewContext.setInterviewStatus("save");
    }
  }, [convertedVideoUrl]);

  const startVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      setMediaStream(stream);
      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // NEW
        await videoRef.current.play();
        startDrawingLoop();
        // Set the background blur on start
        interviewContext.setIsBlurBackgroundEnabled(true);
      }
    } catch (err) {
      console.error("Error accessing media devices.", err);
    }
  };

  const stopVideo = () => {
    const stream = mediaStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
      setMediaStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    if (!recordedVideoBlobUrlValue) {
      startVideo();
    }
    return () => {
      stopVideo();
    };
  }, []);

  useEffect(() => {
    if (closeTrigger || interviewContext.interviewStatus === "stop") {
      stopRecording();
      stopVideo();
    }
  }, [closeTrigger, interviewContext.interviewStatus]);

  // Modified startRecording function with Firefox support
  const startRecording = () => {
    if (!mediaStream) return;
    try {
      let recordingStream;

      // Check if Firefox
      const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");

      if (isFirefox) {
        const canvasStream = canvasRef.current.captureStream(30);
        const audioTrack = mediaStream.getAudioTracks()[0];
        const videoStream = new MediaStream([canvasStream.getVideoTracks()[0]]);

        // Combine video and audio
        recordingStream = new MediaStream([
          videoStream.getVideoTracks()[0],
          audioTrack,
        ]);
      } else {
        const canvasStream = canvasRef.current.captureStream();
        const audioTracks = mediaStream.getAudioTracks();
        recordingStream = new MediaStream([
          ...canvasStream.getVideoTracks(),
          ...audioTracks,
        ]);
      }

      const mediaRecorder = new MediaRecorder(recordingStream, {
        mimeType: "video/webm;codecs=vp8,opus",
        videoBitsPerSecond: 2500000, // Specify bitrate for better quality
      });

      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        setMediaBlobUrl(url);
        setIsVideoRecorded(true);
      };

      // Request larger chunks for better performance
      mediaRecorder.start(1000);
      interviewContext.startVideoRecording();
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting MediaRecorder:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleStopRecording = () => {
    interviewContext.stopVideoRecording();
    stopRecording();
    setIsRecording(false);
    setIsVideoRecorded(true);
    stopVideo();
    setConvertedVideoUrl(null);
    // NEW
    stopSegmentation();
  };

  const fixWebMFormat = async () => {
    if (!mediaBlobUrl) return;
    try {
      const ffmpeg = ffmpegRef.current;

      await ffmpeg.writeFile("input.webm", await fetchFile(mediaBlobUrl));
      await ffmpeg.exec(["-i", "input.webm", "-c", "copy", "output.webm"]);
      const fileData = await ffmpeg.readFile("output.webm");
      const data = new Uint8Array(fileData);
      const url = URL.createObjectURL(
        new Blob([data.buffer], { type: "video/webm" })
      );
      return url;
    } catch (error) {
      console.error("Error converting video:", error.message);
    }
  };

  // Extra Functions =====

  // Function to handle the start recording
  const downloadVideo = async () => {
    let url = recordedVideoBlobUrlValue
      ? recordedVideoBlobUrlValue
      : await convertToMp4(shouldCompressFlag);

    const a = document.createElement("a");
    a.href = url;
    a.download = "interview_video2.webm";
    a.click();
    // Remove after
    window.URL.revokeObjectURL(a.href);
  };

  // Function to handle conversion of webm to mp4
  const convertToMp4 = async (shouldCompress = false) => {
    if (!mediaBlobUrl) return;
    try {
      const startTimestamp = Date.now();
      const ffmpeg = ffmpegRef.current;

      // Write input video file
      await ffmpeg.writeFile("input.webm", await fetchFile(mediaBlobUrl));
      const isFirefox = await isFirefoxBrowser();
      let url = null;
      if (shouldCompress) {
        await ffmpeg.exec([
          "-i",
          "input.webm", // Input file
          "-c:v",
          "libx264", // Use H.264 codec for video compression
          "-preset",
          "ultrafast", // Fastest compression preset
          "-crf",
          "30", // Higher CRF for faster compression (lower quality but smaller size)
          "-c:a",
          "aac", // Use AAC codec for audio
          "-b:a",
          "96k", // Lower audio bitrate for speed
          "output.mp4",
        ]);
      } else if (isFirefox) {
        await ffmpeg.exec(["-i", "input.webm", "-c", "copy", "output.webm"]);
        // Read the output file
        const fileData = await ffmpeg.readFile("output.webm");
        const data = new Uint8Array(fileData);
        url = URL.createObjectURL(
          new Blob([data.buffer], { type: "video/webm" })
        );
      } else {
        await ffmpeg.exec(["-i", "input.webm", "-c", "copy", "output.mp4"]);
        const fileData = await ffmpeg.readFile("output.mp4");
        const data = new Uint8Array(fileData);
        url = URL.createObjectURL(
          new Blob([data.buffer], { type: "video/mp4" })
        );
      }
      return url;
    } catch (error) {
      console.error("Error converting video:", error.message);
    }
  };

  return (
    <>
      {!isVideoRecorded && !recordedVideoBlobUrlValue ? (
        <div>
          <video ref={videoRef} style={{ display: "none" }} muted />
          <canvas
            ref={canvasRef}
            width={640}
            height={480}
            style={{
              width: "100%",
              objectFit: "cover",
              maxHeight: "100%",
              minHeight: "100%",
              borderRadius: "10px",
              border: "1px solid #CAD329",
            }}
          />
        </div>
      ) : (
        <video
          src={recordedVideoBlobUrlValue || mediaBlobUrl}
          controls
          style={{
            width: "100%",
            objectFit: "cover",
            maxHeight: "100%",
            minHeight: "100%",
            borderRadius: "10px",
            border: "1px solid #CAD329",
          }}
        />
      )}
    </>
  );
};

export default InterviewMediaRecorder;
